import axiosHttp from "services/axiosHttp";
import { BACKOFFICE_ORDER_REQUEST } from "../constants/apiPaths";

/**
 * GET /order-request/item-context
 * @param {{ item_code: string, channel_uuid?: string, ref_no?: string }} params — ref_no is the distributor reference (replaces user uuid for analytics-backed min/max qty).
 */
export const getOrderRequestItemContext = async (params) => {
	const { item_code, channel_uuid, ref_no } = params || {};
	if (!item_code) throw new Error("item_code is required");
	const response = await axiosHttp.get(`${BACKOFFICE_ORDER_REQUEST}/item-context`, {
		params: {
			item_code,
			...(channel_uuid ? { channel_uuid } : {}),
			...(ref_no ? { ref_no } : {}),
		},
	});
	return response.data?.data ?? response.data;
};

/**
 * POST /orders/:orderId/revise — replace a placed sales order's line items.
 * @param {{ orderId: number|string, warehouseId?: number|string, items: Array<{item_id:number, quantity:number, unit_id:number}> }} params
 */
export const reviseOrderRequest = async ({ orderId, warehouseId, items }) => {
	if (!orderId) throw new Error("orderId is required");
	if (!Array.isArray(items) || items.length === 0) throw new Error("items must be a non-empty array");
	const body = {
		...(warehouseId != null ? { warehouse_id: warehouseId } : {}),
		items,
	};
	const response = await axiosHttp.post(`orders/${orderId}/revise`, body);
	return response.data?.data ?? response.data;
};

/**
 * GET /channel/:channel_id/unitDetails?keyword=<sku> — resolve the list of sellable units for one item
 * (e.g. Piece / Box of 12 / Carton of 144), so an order line's unit can be switched.
 *
 * IMPORTANT: `sales_order_details.unit_id` / `unit_extra.id` (and the item-context's
 * `preferred_unit.unit_details_id`) all identify a unit by its **unit_details row id**
 * (e.g. 20737), not the master `unit.id` (e.g. 4 for "UNIT"). The dropdown value here must
 * match that same id-space, or a line's already-set unit will never resolve as selected.
 * @returns {Promise<Array<{value:number, label:string, multiplier:number, masterUnitId:number}>>}
 */
export const getItemUnitOptions = async ({ channelUuid, sku }) => {
	if (!channelUuid || !sku) return [];
	const response = await axiosHttp.get(`channel/${channelUuid}/unitDetails`, {
		params: { keyword: sku, limit: 10, page: 1 },
	});
	const data = response.data?.data ?? response.data;
	const item = (data?.Details || []).find((d) => d?.sku === sku) || data?.Details?.[0];
	return (item?.unit_details || [])
		.filter((u) => u?.id != null && u?.unit?.name != null)
		.map((u) => ({
			value: u.id,
			label: u.unit.name,
			multiplier: Number(u.multiplier) || 1,
			masterUnitId: u.unit?.id ?? null,
		}));
};
